/**
 * 테마 — 스타일 위에 놓이는 상위 분류 (테마 → 스타일 2단계, BOOTH-12).
 * 런타임 source-of-truth 는 booth-api DynamoDB `booth-{env}-themes` 테이블.
 * MOCK_THEMES 는 초기 seed 용도 (다국어표 3장 기준 8개).
 */
/**
 * 미분류 테마 — themeId 가 없거나 존재하지 않는 테마를 가리키는 스타일의 lazy default.
 * DynamoDB 에 저장하지 않는 가상 항목. 키오스크는 미분류 스타일이 있을 때만 카드로 노출.
 */
export const UNCATEGORIZED_THEME_ID = "theme_uncategorized";
export const UNCATEGORIZED_THEME = {
    id: UNCATEGORIZED_THEME_ID,
    code: "uncategorized",
    name: "기타",
    nameEn: "Others",
    nameJa: "その他",
    nameZh: "其他",
    thumbnailUrl: "",
    sortOrder: Number.MAX_SAFE_INTEGER,
    isEnabled: true,
};
export const MOCK_THEMES = [
    { id: "theme_hanbok", code: "hanbok", name: "한복", nameEn: "Hanbok", nameJa: "ハンボク", nameZh: "韩服", thumbnailUrl: "", sortOrder: 10, isEnabled: true },
    { id: "theme_aesthetic", code: "aesthetic", name: "한국 미학", nameEn: "Korean Aesthetic", nameJa: "韓国美学", nameZh: "韩国美学", thumbnailUrl: "", sortOrder: 20, isEnabled: true },
    { id: "theme_wedding", code: "wedding", name: "전통 혼례", nameEn: "Traditional Wedding", nameJa: "伝統婚礼", nameZh: "传统婚礼", thumbnailUrl: "", sortOrder: 30, isEnabled: true },
    { id: "theme_royal", code: "royal", name: "왕과 왕비", nameEn: "King & Queen", nameJa: "王と王妃", nameZh: "国王与王后", thumbnailUrl: "", sortOrder: 40, isEnabled: true },
    { id: "theme_kpop", code: "kpop", name: "K-POP", nameEn: "K-POP", nameJa: "K-POP", nameZh: "K-POP", thumbnailUrl: "", sortOrder: 50, isEnabled: true },
    { id: "theme_jeju_fantasy", code: "jeju_fantasy", name: "제주 판타지", nameEn: "Jeju Fantasy", nameJa: "済州ファンタジー", nameZh: "济州奇幻", thumbnailUrl: "", sortOrder: 60, isEnabled: true },
    { id: "theme_jeju_seasons", code: "jeju_seasons", name: "제주 사계", nameEn: "Jeju Seasons", nameJa: "済州四季", nameZh: "济州四季", thumbnailUrl: "", sortOrder: 70, isEnabled: true },
    { id: "theme_jeju_nature", code: "jeju_nature", name: "제주 환경", nameEn: "Jeju Nature", nameJa: "済州自然", nameZh: "济州自然", thumbnailUrl: "", sortOrder: 80, isEnabled: true },
];
/** 스타일의 소속 테마 ID — 없거나 알 수 없는 테마면 미분류. */
export function resolveThemeId(themeId, knownThemeIds) {
    return themeId && knownThemeIds.has(themeId) ? themeId : UNCATEGORIZED_THEME_ID;
}
/**
 * 테마 → 스타일 계층 정규화. API config 응답과 web MOCK 폴백이 같은 규칙을 쓴다.
 * - 스타일의 themeId 가 없거나 테마 목록에 없으면 미분류(UNCATEGORIZED_THEME_ID) 로 채운다 (lazy default).
 * - 미분류 스타일이 하나라도 있으면 가상 테마 UNCATEGORIZED_THEME 을 목록 끝에 붙인다.
 * - 테마·스타일 모두 sortOrder 순 정렬. 입력 배열은 변경하지 않는다.
 */
export function normalizeThemeHierarchy(themes, styles) {
    const known = new Set(themes.map((t) => t.id));
    const normalized = styles
        .map((s) => ({ ...s, themeId: resolveThemeId(s.themeId, known) }))
        .sort(bySortOrder);
    const sortedThemes = [...themes].sort(bySortOrder);
    if (normalized.some((s) => s.themeId === UNCATEGORIZED_THEME_ID)) {
        sortedThemes.push(UNCATEGORIZED_THEME);
    }
    return { themes: sortedThemes, styles: normalized };
}
/**
 * 키오스크 노출 계층 (BOOTH-55). `/api/booth/:slug/config` 와 콘솔 대시보드 `summarizeKiosk` 가 공유.
 * 부스의 **전체** 테마(활성·비활성 모두)와 스타일을 받아 손님 화면에 보일 것만 남긴 뒤 정규화한다.
 * - 비활성 테마는 제외하고, 그 하위 스타일도 함께 숨긴다 (테마 OFF = 하위 캐스케이드 숨김).
 *   → 비활성 테마의 활성 스타일이 "기타" 로 새어 나오지 않는다.
 * - 비활성 스타일은 제외.
 * - themeId 가 없거나 존재하지 않는(삭제된) 테마를 가리키는 활성 스타일만 미분류("기타") 로 노출.
 * 활성 테마만 넘기면 비활성 테마와 삭제된 테마를 구분할 수 없으므로 반드시 전체 테마를 넘길 것.
 */
export function resolveKioskHierarchy(allThemes, styles) {
    const real = allThemes.filter((t) => t.id !== UNCATEGORIZED_THEME_ID);
    const existing = new Set(real.map((t) => t.id));
    const enabledThemes = real.filter((t) => t.isEnabled);
    const enabled = new Set(enabledThemes.map((t) => t.id));
    const visibleStyles = styles.filter((s) => {
        if (!s.isEnabled)
            return false;
        // 존재하는 테마를 가리키면 그 테마가 활성일 때만, 아니면(미지정·삭제됨) 미분류로 노출
        return s.themeId && existing.has(s.themeId) ? enabled.has(s.themeId) : true;
    });
    return normalizeThemeHierarchy(enabledThemes, visibleStyles);
}
/** sortOrder 오름차순, 동률이면 id 순. sortOrder 없는 항목은 뒤로. */
export function bySortOrder(a, b) {
    const ao = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const bo = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
    return ao !== bo ? ao - bo : a.id.localeCompare(b.id);
}
//# sourceMappingURL=theme.js.map